import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCamps,
  setCampsLoading,
  setCampsError,
  setSelectedCamp,
  setAccommodationConfig,
} from '../../../redux/slices/ykVisitSlice';
import ykVisitService from '../services/ykVisitService';
import './YkVisitSelection.css';

const formatDate = (dateStr) => {
  if (!dateStr) return 'TBD';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
};

const effectiveStartDate = (dateStr) => {
  if (!dateStr) return 'Today';
  try {
    const d = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) {
      return today.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return formatDate(dateStr);
  } catch {
    return formatDate(dateStr);
  }
};

const YkVisitSelection = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const centerId = searchParams.get('centerId');
  const country = searchParams.get('country') || 'USA';
  const fetchingRef = useRef(false);
  const lastFetchedCenterId = useRef(null);

  const { camps, campsLoading, campsError, selectedCamp, accommodationConfig } =
    useSelector((state) => state.ykVisit);

  useEffect(() => {
    if (centerId) fetchCamps();
  }, [centerId]);

  const fetchCamps = async () => {
    if (fetchingRef.current) return;
    // Skip if already loaded for this centerId
    if (camps.length > 0 && lastFetchedCenterId.current === centerId) return;
    fetchingRef.current = true;
    try {
      dispatch(setCampsLoading(true));
      const data = await ykVisitService.getCampsByCenter(centerId, country);
      dispatch(setCamps(data || []));
      lastFetchedCenterId.current = centerId;
    } catch (err) {
      dispatch(setCampsError('Failed to load visits. Please try again later.'));
      console.error(err);
    } finally {
      dispatch(setCampsLoading(false));
      fetchingRef.current = false;
    }
  };

  const handleSelectCamp = async (camp) => {
    dispatch(setSelectedCamp(camp));
    try {
      const raw = await ykVisitService.getAccommodationFeeAndCampDate(camp.camp_Id);
      if (raw) {
        const fee = raw.accommodationFee ?? raw.accommodation_Fee ?? raw.Accommodation_Fee ?? raw.AccommodationFee ?? 0;
        dispatch(setAccommodationConfig({
          accommodationFee: parseFloat(fee) || 0,
          campStartDate: raw.campStartDate ?? raw.camp_Start_Date ?? raw.CampStartDate ?? null,
          campEndDate: raw.campEndDate ?? raw.camp_End_Date ?? raw.CampEndDate ?? null,
        }));
      } else {
        dispatch(setAccommodationConfig(null));
      }
    } catch {
      dispatch(setAccommodationConfig(null));
    }
  };

  const handleProceed = () => {
    if (selectedCamp) {
      navigate(`/yk-visit/registration?campId=${selectedCamp.camp_Id}&centerId=${centerId}`);
    }
  };

  return (
    <div className="ykselect-page">
      <div className="container-custom">
        <div className="ykselect-header">
          <button onClick={() => navigate('/yk-visit/center')} className="ykselect-back-btn">
            ← Back to Centers
          </button>
          <h1 className="ykselect-title">Select Visit</h1>
          <p className="ykselect-subtitle">Choose a visit program to register</p>
        </div>

        {campsLoading && (
          <div className="ykvisit-loading">
            <div className="inline-block">
              <div className="ykvisit-spinner"></div>
            </div>
            <p className="ykvisit-loading-text">Loading visits...</p>
          </div>
        )}

        {campsError && (
          <div className="ykvisit-error-banner">
            <p className="ykvisit-error-text">{campsError}</p>
            <button onClick={fetchCamps} className="ykvisit-error-retry">
              Try again
            </button>
          </div>
        )}

        {!campsLoading && camps.length > 0 && (
          <div className="ykselect-layout">
            <div className="ykselect-camp-list">
              {camps.map((camp) => (
                <div
                  key={camp.camp_Id}
                  className={`ykselect-camp-card ${
                    selectedCamp?.camp_Id === camp.camp_Id ? 'selected' : ''
                  }`}
                  onClick={() => handleSelectCamp(camp)}
                >
                  {selectedCamp?.camp_Id === camp.camp_Id && (
                    <div className="ykselect-tick-badge">
                      <svg className="ykselect-tick-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}

                  <div className="ykselect-camp-body">
                    <div className="ykselect-camp-top">
                      <h3 className="ykselect-camp-name">{camp.camp_Name}</h3>
                      {camp.camp_Fee > 0 ? (
                        <span className="ykselect-fee-badge">${camp.camp_Fee}</span>
                      ) : (
                        <span className="ykselect-free-badge">Free</span>
                      )}
                    </div>

                    {camp.camp_Description && (
                      <div
                        className="ykselect-camp-desc"
                        dangerouslySetInnerHTML={{ __html: camp.camp_Description }}
                      />
                    )}

                  </div>
                </div>
              ))}
            </div>

            <div className="ykselect-sidebar">
              {selectedCamp ? (
                <div className="ykselect-summary-card">
                  {/* Header */}
                  <div className="ykselect-summary-header">
                    <div className="ykselect-summary-icon">🙏</div>
                    <div>
                      <h3 className="ykselect-summary-title">Visit Summary</h3>
                      <p className="ykselect-summary-subtitle">Your spiritual journey awaits</p>
                    </div>
                  </div>

                  {/* Program name */}
                  <div className="ykselect-summary-program">
                    <span className="ykselect-summary-program-name">{selectedCamp.camp_Name}</span>
                  </div>

                  {/* Visit fee */}
                  <div className="ykselect-summary-row">
                    <span className="ykselect-summary-row-icon">🎟️</span>
                    <div className="ykselect-summary-row-body">
                      <span className="ykselect-summary-row-label">Registration Fee</span>
                      <span className={`ykselect-summary-row-value ${selectedCamp.camp_Fee > 0 ? '' : 'ykselect-free-tag'}`}>
                        {selectedCamp.camp_Fee > 0 ? `$${selectedCamp.camp_Fee}` : 'Free'}
                      </span>
                    </div>
                  </div>

                  {/* Accommodation */}
                  {accommodationConfig && (
                    <div className="ykselect-summary-row">
                      <span className="ykselect-summary-row-icon">🛏️</span>
                      <div className="ykselect-summary-row-body">
                        <span className="ykselect-summary-row-label">Accommodation</span>
                        <span className="ykselect-summary-accom-price">
                          ${parseFloat(accommodationConfig.accommodationFee || 0).toFixed(0)}
                          <span className="ykselect-summary-accom-unit"> / night / person</span>
                        </span>
                        <div className="ykselect-summary-accom-perks">
                          <span className="ykselect-summary-perk">🍽️ Meals included</span>
                          <span className="ykselect-summary-perk">Optional</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <button onClick={handleProceed} className="ykselect-proceed-btn">
                    Proceed to Registration
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ marginLeft: 6 }}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="ykselect-empty-card">
                  <div className="ykselect-empty-icon">🙏</div>
                  <p className="ykselect-empty-hint">Select a visit program to view details and proceed</p>
                </div>
              )}
            </div>
          </div>
        )}

        {!campsLoading && camps.length === 0 && !campsError && (
          <div className="ykselect-no-camps">
            <div className="ykselect-no-camps-icon">🙏</div>
            <p className="ykselect-no-camps-text">No visit programs available for this center.</p>
            <button onClick={() => navigate('/yk-visit/center')} className="ykselect-no-camps-link">
              ← Choose a different center
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default YkVisitSelection;
